import { useMemo } from 'react';
import { ExternalLink } from 'lucide-react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Badge } from '@/components/common/Badge/Badge';
import { Button } from '@/components/common/Button/Button';
import { Card } from '@/components/common/Card/Card';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { LoadingState } from '@/components/common/LoadingState/LoadingState';
import { PageBreadcrumb } from '@/components/layout/PageBreadcrumb/PageBreadcrumb';
import { StepHeader } from '@/components/layout/StepHeader/StepHeader';
import { topics as topicCatalog } from '@/data/topics';
import { useCandidateProposal } from '@/hooks/use-candidate-proposal';
import { useSeo } from '@/hooks/useSeo';
import {
  buildCandidateProposalDescription,
  buildCandidateProposalJsonLd,
  buildCandidateTopicProposalDescription,
  buildPageTitle,
} from '@/lib/seo';
import {
  buildCandidateProposalPath,
  buildCandidateProposalTopicPath,
  findTopicBySlug,
} from '@/utils/candidateProposalRoute';

import styles from '@/styles/positions.module.css';

function parsePage(searchParams: URLSearchParams): number {
  const rawPage = searchParams.get('page');
  const page = rawPage ? Number.parseInt(rawPage, 10) : 1;

  if (!Number.isFinite(page) || page < 1) {
    return 1;
  }

  return page;
}

export function CandidateProposalPage(): JSX.Element {
  const navigate = useNavigate();
  const params = useParams<{ slug: string; topicSlug?: string }>();
  const [searchParams] = useSearchParams();
  const page = useMemo(() => parsePage(searchParams), [searchParams]);
  const state = useCandidateProposal(params.slug ?? null);
  const candidate = state.status === 'success' ? state.candidate : null;
  const topic = params.topicSlug ? findTopicBySlug(topicCatalog, params.topicSlug) : null;
  const isTopicRoute = Boolean(params.topicSlug);
  const proposalPath = params.slug ? buildCandidateProposalPath(params.slug) : '/';
  const requestedPath = params.slug
    ? isTopicRoute && params.topicSlug && topic
      ? buildCandidateProposalTopicPath(params.slug, topic.label)
      : buildCandidateProposalPath(params.slug)
    : '/';
  const isIndexable = !searchParams.has('page');
  const proposalDescription = candidate
    ? topic
      ? buildCandidateTopicProposalDescription(candidate.name, topic.label, candidate.party)
      : buildCandidateProposalDescription(candidate.name, candidate.party)
    : 'Leia o documento oficial do candidato com navegação por página e fontes do TSE.';
  const structuredData = useMemo(() => {
    if (!candidate) {
      return undefined;
    }

    return buildCandidateProposalJsonLd({
      candidateName: candidate.name,
      candidateParty: candidate.party,
      canonicalPath: requestedPath,
      description: proposalDescription,
      image: candidate.photoUrl,
      topicLabel: topic?.label,
    });
  }, [candidate, proposalDescription, requestedPath, topic?.label]);

  useSeo({
    title: candidate
      ? buildPageTitle(topic ? `${candidate.name} | ${topic.label} | Proposta oficial` : `${candidate.name} | Proposta oficial`)
      : buildPageTitle('Proposta oficial'),
    description: proposalDescription,
    ogTitle: candidate
      ? topic
        ? `${candidate.name} | ${topic.label} | Proposta oficial`
        : `${candidate.name} | Proposta oficial`
      : 'Proposta oficial',
    ogDescription: proposalDescription,
    canonicalPath: requestedPath,
    image: candidate?.photoUrl,
    imageAlt: candidate ? `${candidate.name} no Verde Confirma` : undefined,
    ogType: 'article',
    indexable: isIndexable,
    structuredData,
  });

  if (!params.slug) {
    return <Navigate to="/" replace />;
  }

  if (isTopicRoute && params.topicSlug && !topic) {
    return <Navigate to={proposalPath} replace />;
  }

  if (state.status === 'loading') {
    return (
      <section className={styles.questionCard}>
        <PageBreadcrumb items={[{ label: 'Início', to: '/' }, { label: 'Proposta' }]} />

        <StepHeader kicker="Proposta" title="Carregando proposta" description="Consultando o documento privado do candidato." />
        <LoadingState title="Abrindo proposta" messages={['Buscando o arquivo', 'Preparando a visualização', 'Carregando o PDF']} />
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <section className={styles.questionCard}>
        <PageBreadcrumb items={[{ label: 'Início', to: '/' }, { label: 'Proposta' }]} />

        <StepHeader
          kicker="Proposta"
          title="Não foi possível carregar a proposta"
          description="Tente novamente em instantes."
          actions={
            <Button type="button" variant="secondary" onClick={() => navigate(-1)}>
              Voltar
            </Button>
          }
        />
        <EmptyState tone="danger" title="Erro ao carregar o documento" description={state.error} />
      </section>
    );
  }

  const viewerUrl = `${state.document.url}#page=${page}`;

  return (
    <section className={styles.questionCard}>
      <PageBreadcrumb
        items={
          topic
            ? [
                { label: 'Início', to: '/' },
                { label: state.candidate.name, to: proposalPath },
                { label: topic.label },
              ]
            : [{ label: 'Início', to: '/' }, { label: state.candidate.name }]
        }
      />

      <StepHeader
        kicker="Proposta"
        title={state.candidate.name}
        description={proposalDescription}
        actions={
          <Button type="button" variant="secondary" onClick={() => navigate(-1)}>
            Voltar
          </Button>
        }
      />

      <Card className={styles.proposalDocumentCard}>
        <div className={styles.proposalDocumentHeader}>
          <div>
            <Badge tone="primary">{state.candidate.party}</Badge>
            <p className={styles.proposalDocumentMeta}>
              <a href="https://divulgacandcontas.tse.jus.br/divulga/#/candidato/BR/BR/20322002026" target="_blank" rel="noreferrer">
                Documento obtido no site oficial do TSE
              </a>
            </p>
          </div>

          <div className={styles.proposalDocumentHeaderActions}>
            {page > 1 ? <Badge tone="neutral">Página {page}</Badge> : null}
            <Button href={state.document.url} external variant="secondary">
              <ExternalLink size={14} />
              Abrir em nova aba
            </Button>
          </div>
        </div>

        <iframe title={`Proposta de ${state.candidate.name}`} src={viewerUrl} className={styles.proposalDocumentFrame} />

        <p className={styles.proposalDocumentNotice}>
          Este documento pode não refletir a versão mais recente. Em caso de dúvida, consulte os portais oficiais.
        </p>

        <div className={styles.proposalDocumentFooter}>
          <span>{state.document.filePath}</span>
        </div>
      </Card>
    </section>
  );
}
